import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import Papa from 'papaparse';
import { useAuth } from '../context/AuthContext';
import { assignUniqueClientSlug } from '../shared/clients/companyClientRouting';
import { createClient, listClients } from '../api/companyRecords';

const ImportClientsDialog = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const { user, companyData } = useAuth(); // Use the useAuth hook

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setError(null);
  };

  const handleImport = () => {
    if (!file) {
      setError('Please select a file to import.');
      return;
    }

    setIsProcessing(true);
    Papa.parse(file, {
      header: true,
      complete: processResults,
      error: (error) => {
        setIsProcessing(false);
        setError(`Error parsing CSV file: ${error.message}`);
      }
    });
  };

  const processResults = async (results) => {
    if (results.errors.length > 0) {
      setIsProcessing(false);
      setError('Error parsing CSV file. Please check the file format.');
      return;
    }

    try {
      if (!user || !companyData || !companyData.id) {
        throw new Error('No authenticated user or company ID');
      }

      let importedCount = 0;
      let skippedCount = 0;
      const existingClients = await listClients({ companyId: companyData.id, archived: 'all' });
      const existingEmails = new Set(
        existingClients.map((entry) => String(entry.email || '').trim().toLowerCase()).filter(Boolean)
      );

      for (const client of results.data) {
        const emailKey = String(client.email || '').trim().toLowerCase();
        if (emailKey && existingEmails.has(emailKey)) {
          skippedCount++;
          continue;
        }

        const fullName = `${client.firstName} ${client.lastName}`.trim();
        const slug = assignUniqueClientSlug(fullName, existingClients);
        existingClients.push({ slug });
        if (emailKey) existingEmails.add(emailKey);

        await createClient({
          companyId: companyData.id,
          client: {
            firstName: client.firstName,
            lastName: client.lastName,
            name: fullName,
            slug,
            email: client.email,
            phone: client.phone,
            searchName: fullName.toLowerCase(),
            archived: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        });
        importedCount++;
      }

      setImportResult({ importedCount, skippedCount });
    } catch (err) {
      console.error("Error importing clients:", err);
      setError(`Error importing clients: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Clients</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Select a CSV file to import clients. The file should have the following columns:
          First Name, Last Name, Email, Phone, Category
        </Typography>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ marginBottom: '1rem' }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        {isProcessing && (
          <Box display="flex" alignItems="center" mt={2}>
            <CircularProgress size={24} style={{ marginRight: '1rem' }} />
            <Typography>Processing file...</Typography>
          </Box>
        )}
        {importResult && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Imported {importResult.importedCount} clients. Skipped {importResult.skippedCount} existing clients.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleImport} color="primary" disabled={!file || isProcessing}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportClientsDialog;